import {
    CodeBlock,
    BlockType,
    SettingType,
    Define,
    ValidationMessage,
    ValidationMessageType,
    SettingState,
    TibboDataType,
} from '@appblocks/codegeneration/types';
import { TIBBO_DEVICE_ID } from '@appblocks/platforms/PlatformUtils';
import BaseFeature from '@appblocks/codegeneration/BaseFeature';


export default class Wifi extends BaseFeature {
    constructor() {
        super();
        this.name = 'Wi-Fi';
        this.id = 'wifi';
        this.icon = 'FaWifi';
        this.priority = 99;
        this.requirements = ['wifi', 'zephyr'];
        this.capabilities = ['network'];
        const settings = [
            {
                id: 'ENABLED',
                name: 'Wi-Fi Enabled',
                settingType: SettingType.OPTION,
                required: true,
                default: '0',
                options: [
                    {
                        name: 'Disabled',
                        value: '0',
                    },
                    {
                        name: 'Enabled',
                        value: '1',
                    },
                ],
            },
            {
                id: 'MODE',
                name: 'Mode',
                settingType: SettingType.OPTION,
                required: true,
                // readonly: true,
                default: 'station',
                options: [
                    {
                        name: 'Station',
                        value: 'station',
                    },
                    {
                        name: 'AP',
                        value: 'ap',
                    },
                ],
            },
            {
                id: 'DHCP',
                name: 'DHCP',
                settingType: SettingType.OPTION,
                dataType: TibboDataType.SETTING,
                setting: {
                    SETTINGID: 'WIFCP',
                    NAME: 'Wifi DHCP',
                    SETTINGTYPE: 'float',
                    DATATYPE: 'B',
                    MIN: 0,
                    MAX: 1,
                    DEFAULT: '1',
                },
                UIControl: {
                    CONTROL: 'STATIC',
                    OPTIONS: [
                        {
                            name: 'Disabled',
                            value: '0',
                        },
                        {
                            name: 'Enabled',
                            value: '1',
                        },
                    ],
                },
                required: true,
                default: '1',
                options: [
                    {
                        name: 'Disabled',
                        value: '0',
                    },
                    {
                        name: 'Enabled',
                        value: '1',
                    },
                ],
            },
            {
                id: 'IP',
                name: 'IP Address',
                settingType: SettingType.IP,
                dataType: TibboDataType.SETTING,
                setting: {
                    SETTINGID: 'WIFIP',
                    NAME: 'Wifi IP',
                    SETTINGTYPE: 'string',
                    DATATYPE: 'S',
                    MAX: 16,
                    DEFAULT: '1.0.0.1',
                },
                UIControl: {
                    CONTROL: 'IPCTRL',
                },
                required: false,
                default: '1.0.0.1',
            },
            {
                id: 'NETMASK',
                name: 'Netmask',
                settingType: SettingType.IP,
                dataType: TibboDataType.SETTING,
                setting: {
                    SETTINGID: 'WIFNM',
                    NAME: 'Wifi Netmask',
                    SETTINGTYPE: 'string',
                    DATATYPE: 'S',
                    MAX: 16,
                    DEFAULT: '255.255.255.0',
                },
                UIControl: {
                    CONTROL: 'IPCTRL',
                },
                required: false,
                default: '255.255.255.0',
            },
            {
                id: 'GATEWAY',
                name: 'Gateway',
                settingType: SettingType.IP,
                dataType: TibboDataType.SETTING,
                setting: {
                    SETTINGID: 'WIFGW',
                    NAME: 'Wifi Gateway',
                    SETTINGTYPE: 'string',
                    DATATYPE: 'S',
                    MAX: 16,
                    DEFAULT: '1.0.0.1',
                },
                UIControl: {
                    CONTROL: 'IPCTRL',
                },
                required: false,
                default: '1.0.0.1',
            },
            {
                id: 'WAP',
                name: 'Access Point SSID',
                settingType: SettingType.STRING,
                dataType: TibboDataType.SETTING,
                setting: {
                    SETTINGID: 'WIFAP',
                    NAME: 'Wifi AP SSID',
                    SETTINGTYPE: 'string',
                    DATATYPE: 'S',
                    MAX: 32,
                },
                UIControl: {
                    CONTROL: 'EDIT',
                },
                required: true,
                default: '',
            },
            {
                id: 'WPW',
                name: 'Password',
                settingType: SettingType.PASSWORD,
                dataType: TibboDataType.SETTING,
                setting: {
                    SETTINGID: 'WIFPW',
                    NAME: 'Wifi Password',
                    SETTINGTYPE: 'string',
                    DATATYPE: 'S',
                    MIN: 0,
                    MAX: 64,
                },
                UIControl: {
                    CONTROL: 'PASSWORD',
                },
                required: true,
                default: '',
            },
        ];


        this.settings = this.settings.concat(settings);
    }

    validate(): Array<ValidationMessage> {
        const errors: Array<ValidationMessage> = [];
        const { project } = this;

        return errors;
    }

    getState(settingId: string): SettingState {
        if (settingId !== 'ENABLED' && this.getSettingValue('ENABLED').toString() === '0') {
            return SettingState.DISABLED;
        }
        switch (settingId) {
            case 'ENABLED':
                return SettingState.ENABLED;
            case 'CHANNEL':
                if (this.getSettingValue('MODE') === 'ap') {
                    return SettingState.ENABLED;
                }
                return SettingState.HIDDEN;
            default:
                return SettingState.ENABLED;
        }
    }

    getCodeBlocks() {
        const blocks: Array<CodeBlock> = [];

        if (this.getSettingValue('ENABLED').toString() === '0') {
            return blocks;
        }

        const { project } = this;
        const SSID = this.getSettingValue('WAP');
        const SECURITY = this.getSettingValue('WSEC');
        const PASSWORD = this.getSettingValue('WPW');
        const DHCP = this.getSettingValue('DHCP');
        const IP = this.getSettingValue('IP');
        const GATEWAY = this.getSettingValue('GATEWAY');
        const NETMASK = this.getSettingValue('NETMASK');
        let DHCPIsLinked = false;
        for (let i = 0; i < this.project.features.length; i++) {
            const feature = this.project.features[i];
            if (feature.id === 'linked_setting') {
                if (feature.getSettingValue('FEATURE') === this.getSettingValue('ID')
                    && feature.getSettingValue('SETTINGID') === 'DHCP') {
                    DHCPIsLinked = true;
                }
            }
        }

        blocks.push({
            location: 'APP_OVERLAY',
            blockType: BlockType.CODE,
            template: `
&wifi {
    status = "okay";
};
`,
        });

        blocks.push({
            location: 'PRJ_CONF',
            blockType: BlockType.CODE,
            template: `
CONFIG_WIFI=y
CONFIG_INIT_STACKS=y
CONFIG_NET_L2_WIFI_MGMT=y

CONFIG_NETWORKING=y
CONFIG_NET_IPV4=y
CONFIG_NET_IPV6=n
CONFIG_NET_UDP=y
CONFIG_NET_TCP=y

CONFIG_NET_SOCKETS=y
# CONFIG_NET_LOG=y

# Use DHCP for IPv4
CONFIG_NET_DHCPV4=y

CONFIG_NET_CONFIG_AUTO_INIT=y
# Or assign a static IP address (useful for testing)
# Following line must be enabled, otherwise WiFi connection fails with -1.
# CONFIG_NET_CONFIG_SETTINGS=y
# CONFIG_NET_CONFIG_NEED_IPV4=y
# CONFIG_NET_CONFIG_MY_IPV4_ADDR=${IP}
# CONFIG_NET_CONFIG_MY_IPV4_GW=${GATEWAY}
# CONFIG_NET_CONFIG_MY_IPV4_NETMASK=${NETMASK}

# Enabling logging can be helpful for debugging
#CONFIG_WIFI_LOG_LEVEL_ERR=y
#CONFIG_NET_IPV6_LOG_LEVEL_DBG=y

CONFIG_NET_TX_STACK_SIZE=2048
CONFIG_NET_RX_STACK_SIZE=2048

CONFIG_NET_PKT_RX_COUNT=20
CONFIG_NET_PKT_TX_COUNT=20

CONFIG_NET_BUF_RX_COUNT=10
CONFIG_NET_BUF_TX_COUNT=30

CONFIG_NET_MAX_CONTEXTS=5

# Added to prevent error with missing #include <ethernet/eth_stats.h> for esp_wifi_drv.c
# Adding this line will cause CMAKE Warning: No SOURCES given to Zephyr library: drivers__ethernet
# Just ignore.
CONFIG_NET_L2_ETHERNET=y

# Enable net conn manager
CONFIG_NET_CONNECTION_MANAGER=y
`,
        });

        blocks.push({
            location: 'GLOBAL_INCLUDES',
            blockType: BlockType.CODE,
            template: `
#include <zephyr/net/net_if.h>
#include <zephyr/net/wifi_mgmt.h>
#include <zephyr/net/net_event.h>

#include <zephyr/net/socket.h>

#include <zephyr/net/net_pkt.h>
#include <zephyr/net/net_mgmt.h>
#include <zephyr/net/conn_mgr_monitor.h>
`,
        });

        blocks.push({
            location: 'FUNCTION_DEFINITIONS',
            template: `
static void handle_wifi_connect_result(struct net_mgmt_event_callback *cb)
{
    const struct wifi_status *status = (const struct wifi_status *)cb->info;

    if (status->status)
    {
        LOG_INF("Connection request failed (%d)\\n", status->status);
        wifi_connect();
    }
    else
    {
        LOG_INF("WiFi Connected\\n");
        if (!dhcp_enabled) {
            interface_params.interface = 2;
            interface_params.state = true;
            k_work_submit(&interface_set_work);
            net_if_ready = true;
        }
        // k_sem_give(&wifi_connected);
    }
}

void wifi_connect(void)
{
    struct net_if *iface = net_if_get_default();

    struct wifi_connect_req_params wifi_params = {0};

    wifi_params.ssid = (const uint8_t*)SSID.c_str();
    wifi_params.psk = (const uint8_t*)PSK.c_str();
    wifi_params.ssid_length = SSID.length();
    wifi_params.psk_length = PSK.length();
    wifi_params.channel = WIFI_CHANNEL_ANY;
    wifi_params.security = WIFI_SECURITY_TYPE_PSK;
    wifi_params.band = WIFI_FREQ_BAND_2_4_GHZ;
    wifi_params.mfp = WIFI_MFP_OPTIONAL;

    LOG_INF("Connecting to SSID: %s\\n", wifi_params.ssid);

    if (net_mgmt(NET_REQUEST_WIFI_CONNECT, iface, &wifi_params, sizeof(struct wifi_connect_req_params)))
    {
        LOG_INF("WiFi Connection Request Failed\\n");
    }
}

static void handle_wifi_disconnect_result(struct net_mgmt_event_callback *cb)
{
    const struct wifi_status *status = (const struct wifi_status *)cb->info;

    if (status->status)
    {
        LOG_INF("Disconnection request (%d)\\n", status->status);
        wifi_connect();
    }
    else
    {
        LOG_INF("WiFi Disconnected\\n");
        // k_sem_take(&wifi_connected, K_NO_WAIT);
        wifi_connect();
    }
    net_if_ready = false;
}

static void handle_ipv4_result(struct net_if *iface)
{
    int i = 0;

    for (i = 0; i < NET_IF_MAX_IPV4_ADDR; i++)
    {

        char buf[NET_IPV4_ADDR_LEN];

        

        LOG_INF("IPv4 address: %s\\n",
               net_addr_ntop(AF_INET,
                             &iface->config.ip.ipv4->unicast[i].ipv4.address.in_addr,
                             buf, sizeof(buf)));
        LOG_INF("Subnet: %s\\n",
               net_addr_ntop(AF_INET,
                             &iface->config.ip.ipv4->unicast[i].netmask,
                             buf, sizeof(buf)));
        LOG_INF("Router: %s\\n",
               net_addr_ntop(AF_INET,
                             &iface->config.ip.ipv4->gw,
                             buf, sizeof(buf)));

        // determine if the ipv4 address is dhcp
        if (dhcp_enabled) {
            if (iface->config.ip.ipv4->unicast[i].ipv4.addr_type != NET_ADDR_DHCP)
            {
                continue;
            }
        }
        // interface_set(2, true);

        interface_params.interface = 2;
        interface_params.state = true;
        k_work_submit(&interface_set_work);

        net_if_ready = true;
    }

    //k_sem_give(&ipv4_address_obtained);
}

static void wifi_mgmt_event_handler(struct net_mgmt_event_callback *cb, uint32_t mgmt_event, struct net_if *iface)
{
    switch (mgmt_event)
    {

    case NET_EVENT_WIFI_CONNECT_RESULT:
        handle_wifi_connect_result(cb);
        break;

    case NET_EVENT_WIFI_DISCONNECT_RESULT:
        handle_wifi_disconnect_result(cb);
        break;

    case NET_EVENT_IPV4_ADDR_ADD:
        handle_ipv4_result(iface);
        break;

    default:
        break;
    }
}

void wifi_status(void)
{
    struct net_if *iface = net_if_get_default();

    struct wifi_iface_status status = {0};

    if (net_mgmt(NET_REQUEST_WIFI_IFACE_STATUS, iface, &status, sizeof(struct wifi_iface_status)))
    {
        LOG_INF("WiFi Status Request Failed\\n");
    }

    if (status.state >= WIFI_STATE_ASSOCIATED)
    {
        LOG_INF("SSID: %-32s\\n", status.ssid);
        LOG_INF("Band: %s\\n", wifi_band_txt(status.band));
        LOG_INF("Channel: %d\\n", status.channel);
        LOG_INF("Security: %s\\n", wifi_security_txt(status.security));
        LOG_INF("RSSI: %d\\n", status.rssi);
    }
}

void wifi_disconnect(void)
{
    struct net_if *iface = net_if_get_default();

    if (net_mgmt(NET_REQUEST_WIFI_DISCONNECT, iface, NULL, 0))
    {
        LOG_INF("WiFi Disconnection Request Failed\\n");
    }
}
            

static void net_l4_evt_handler(struct net_mgmt_event_callback *cb,
            uint32_t mgmt_event, struct net_if *iface)
{
    // LOG_INF("L4 event handler called with event %d", mgmt_event);
    switch (mgmt_event) {
    case NET_EVENT_L4_CONNECTED:
        //k_sem_give(&net_conn_sem);
        LOG_INF("Network connectivity up!");
        handle_ipv4_result(iface);
        break;
    case NET_EVENT_IPV4_ADDR_ADD:
        handle_ipv4_result(iface);
        break;        
    case NET_EVENT_L4_DISCONNECTED:
        LOG_INF("Network connectivity down!");
        break;
    default:
        break;
    }
}
`,
        });

        blocks.push({
            location: 'FUNCTION_DECLARATIONS',
            blockType: BlockType.CODE,
            template: `
void wifi_connect(void);
`,
        });

        blocks.push({
            location: 'VARIABLE_DECLARATIONS',
            blockType: BlockType.CODE,
            template: `
static struct net_mgmt_event_callback wifi_cb;
static struct net_mgmt_event_callback ipv4_cb;
static struct net_mgmt_event_callback net_l4_mgmt_cb;
string SSID;
string PSK;
static bool dhcp_enabled = false;
`,
        });

        blocks.push({
            location: 'GLOBAL_DEFINES',
            blockType: BlockType.CODE,
            template: `
#define NET_L4_EVENT_MASK (NET_EVENT_L4_CONNECTED | NET_EVENT_L4_DISCONNECTED)

`,
        });

        blocks.push({
            location: 'boot',
            blockType: BlockType.CODE,
            template: `
dhcp_enabled = true;

SSID = ${SSID};
PSK = ${PASSWORD};

struct net_if *iface = net_if_get_default();
net_mgmt_init_event_callback(&wifi_cb, wifi_mgmt_event_handler,
                                    NET_EVENT_WIFI_CONNECT_RESULT | NET_EVENT_WIFI_DISCONNECT_RESULT);
net_mgmt_add_event_callback(&wifi_cb);

dhcp_enabled = ${DHCP} == 1;

struct in_addr addr4;
net_addr_pton(AF_INET, string(${GATEWAY}).c_str(), &addr4);
net_if_ipv4_set_gw(iface, &addr4);
net_addr_pton(AF_INET, string(${NETMASK}).c_str(), &addr4);
net_if_ipv4_set_netmask_by_addr(iface, &addr4, &addr4);

if (!dhcp_enabled) {
    
    net_addr_pton(AF_INET, string(${IP}).c_str(), &addr4);
} else {
    net_mgmt_init_event_callback(&ipv4_cb, net_l4_evt_handler, NET_EVENT_IPV4_ADDR_ADD);
    net_mgmt_init_event_callback(&net_l4_mgmt_cb, &net_l4_evt_handler, NET_L4_EVENT_MASK);
    net_mgmt_add_event_callback(&ipv4_cb);
    net_mgmt_add_event_callback(&net_l4_mgmt_cb);

    net_addr_pton(AF_INET, "", &addr4);
}
net_if_ipv4_addr_add(iface, &addr4, NET_ADDR_OVERRIDABLE, 0);


LOG_INF("Bringing up network..");



wifi_connect();

if (dhcp_enabled) {
    net_dhcpv4_start(iface);
} else {
    net_dhcpv4_stop(iface);
}

`,
        });

        return blocks;
    }
}
